import { DataGrid, LabeledValue, Modal } from "@msys/ui";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link } from "react-router-dom";
import { useLatest } from "react-use";
import { buildDocPath } from "../../../utils.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { TemplateVariantUpdateProtocolEntryFragment } from "./useTemplateVariantUpdateProtocol.generated.js";

interface Props {
  entries: TemplateVariantUpdateProtocolEntryFragment[];
  toVersionNumber: number;
  pathToDocList: string;
  handleClose(): void;
}

export const ShowTemplateVariantProtocolModal = ({
  entries,
  toVersionNumber,
  pathToDocList,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Global", "Templates"]);

  const stateStore = useDataGridStateStore("ShowTemplateVariantProtocolModal");
  const handleCloseLatest = useLatest(handleClose);

  const columns = React.useMemo(
    (): GridColDef<TemplateVariantUpdateProtocolEntryFragment>[] => [
      {
        field: "toItemTitle",
        headerName: t("Item", { ns: "Templates" }),
        flex: 1,
        sortable: false,
      },
      {
        field: "problemPart",
        headerName: t("Problem", { ns: "Templates" }),
        flex: 1,
        sortable: false,
        valueGetter: (value, entry) =>
          `${entry.problemPart} / ${entry.problemPropKey ?? entry.problemAttributeName}`,
      },
      {
        field: "problemReason",
        headerName: t("Reason", { ns: "Templates" }),
        flex: 3,
        sortable: false,
      },
      {
        field: "toVersionNumber",
        headerName: t("Version", { ns: "Templates" }),
        flex: 1,
        sortable: false,
        valueGetter: (value, entry) =>
          `${entry.fromVersionNumber} → ${entry.toVersionNumber}`,
      },
      {
        field: "action",
        headerName: "",
        sortable: false,
        groupable: false,
        flex: 0,
        minWidth: 34,
        maxWidth: 34,
        renderCell({ row: entry }) {
          return entry.toVersionNumber === toVersionNumber ? (
            <IconButton
              color="primary"
              size={"small"}
              sx={{ marginLeft: "-10px" }}
              component={Link}
              to={`${buildDocPath(
                `${pathToDocList}/${entry.templateId}`,
                entry.toVersionNumber
              )}/items/${entry.itemId}`}
              onClick={e => {
                if (!e.ctrlKey && !e.metaKey) {
                  handleCloseLatest.current();
                }
              }}
            >
              <Tooltip title={t("See more", { ns: "Global" })}>
                <VisibilityIcon />
              </Tooltip>
            </IconButton>
          ) : null;
        },
      },
    ],
    [handleCloseLatest, pathToDocList, t, toVersionNumber]
  );

  return (
    <Modal
      title={t("Update protocol", { ns: "Templates" })}
      handleClose={handleClose}
      maxWidth="xl"
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
    >
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={2}>
          {entries[0] ? (
            <LabeledValue label={t("Template", { ns: "Templates" })}>
              {entries[0].templateTitle}
            </LabeledValue>
          ) : null}
          <LabeledValue label={t("Updated to version", { ns: "Templates" })}>
            {toVersionNumber}
          </LabeledValue>
        </Stack>
        <DataGrid<TemplateVariantUpdateProtocolEntryFragment>
          stateStore={stateStore}
          columns={columns}
          // disableColumnMenu
          disableVirtualization
          rows={entries}
          paginationMode="client"
          sortingMode="client"
          disableRowSelectionOnClick
        />
      </Stack>
    </Modal>
  );
};
